import { useState } from 'react';
import { FileSpreadsheet, Plus, FileText, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { DeclarationList } from './components/DeclarationList';
import { DeclarationFilters, type DeclarationFiltersState } from './components/DeclarationFilters';
import { NewDeclarationModal } from './components/NewDeclarationModal';
import { DeclarationPreview } from '../../components/declarations/DeclarationPreview';
import { Modal } from '../../components/ui/Modal';
import { useDeclarations } from '../../hooks/useDeclarations';
import { useFiscalYear } from '../../hooks/useFiscalYear';
import { useReportActions } from '../../hooks/useReportActions';
import { useCurrency } from '../../hooks/useCurrency';
import type { Declaration } from '../../types/declarations';

export function DeclarationsPage() {
  const [showNewDeclarationModal, setShowNewDeclarationModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDeclaration, setSelectedDeclaration] = useState<Declaration | null>(null);
  const [filters, setFilters] = useState<DeclarationFiltersState>({
    type: 'all',
    category: 'all',
    period: 'all',
    status: 'all'
  });
  const { handleDownload, handlePrint } = useReportActions();
  const { format } = useCurrency();
  const { currentFiscalYear } = useFiscalYear();
  const { declarations, loading } = useDeclarations({ fiscalYear: currentFiscalYear });

  // Protection contre les données undefined
  const safeDeclarations = Array.isArray(declarations) ? declarations : [];

  // Calculer les statistiques à partir des données réelles
  const stats = {
    totalDeclarations: safeDeclarations.length,
    totalAmount: safeDeclarations.reduce((sum, d) => sum + d.amount, 0),
    totalFees: safeDeclarations.reduce((sum, d) => sum + (d.additionalFees || 0), 0),
    totalPenalties: safeDeclarations.reduce((sum, d) => sum + (d.penalties || 0), 0),
    byStatus: {
      validated: safeDeclarations.filter(d => d.status === 'validated').length,
      pending: safeDeclarations.filter(d => d.status === 'pending').length,
      rejected: safeDeclarations.filter(d => d.status === 'rejected').length,
      draft: safeDeclarations.filter(d => d.status === 'draft').length,
      submitted: safeDeclarations.filter(d => d.status === 'submitted').length
    },
    byCategory: {
      direct_tax: safeDeclarations.filter(d => ['IBP', 'IPR', 'IRCM', 'IRVM', 'IPF'].includes(d.type)).length,
      indirect_tax: safeDeclarations.filter(d => ['TVA', 'TPI', 'TCR', 'TE', 'TAD', 'TRD'].includes(d.type)).length,
      social_contribution: safeDeclarations.filter(d => ['CNSS', 'INPP', 'ONEM'].includes(d.type)).length,
      special_tax: safeDeclarations.filter(d => ['TSD', 'TPU', 'TSE', 'AUTRES'].includes(d.type)).length
    }
  };

  const handlePreviewDeclaration = (declaration: Declaration) => {
    setSelectedDeclaration(declaration);
    setShowPreviewModal(true);
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Déclarations Fiscales
          </h1>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
            Gestion des déclarations fiscales RDC - Conformité SYSCOHADA/OHADA
          </p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => setShowNewDeclarationModal(true)}
          className="w-full sm:w-auto"
        >
          Nouvelle Déclaration
        </Button>
      </div>

      {/* Fiscal Year Indicator */}
      {currentFiscalYear && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Année Fiscale Courante : {currentFiscalYear.code || new Date(currentFiscalYear.startDate).getFullYear()}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Du {new Date(currentFiscalYear.startDate).toLocaleDateString('fr-FR')} 
                au {new Date(currentFiscalYear.endDate).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              currentFiscalYear.status === 'open' 
                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
            }`}>
              {currentFiscalYear.status === 'open' ? 'Ouverte' : 'Fermée'}
            </span>
          </div>
        </div>
      )}

      {/* Compact Statistics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground">Total</p>
              <p className="text-lg md:text-xl font-bold text-card-foreground">
                {stats.totalDeclarations}
              </p>
            </div>
            <FileText className="h-6 w-6 md:h-8 md:w-8 text-primary" />
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground">Montant</p>
              <p className="text-lg md:text-xl font-bold text-card-foreground">
                {format(stats.totalAmount)}
              </p>
            </div>
            <DollarSign className="h-6 w-6 md:h-8 md:w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground">Validées</p>
              <p className="text-lg md:text-xl font-bold text-green-600 dark:text-green-400">
                {stats.byStatus.validated}
              </p>
            </div>
            <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm font-medium text-muted-foreground">En Attente</p>
              <p className="text-lg md:text-xl font-bold text-yellow-600 dark:text-yellow-400">
                {stats.byStatus.pending + stats.byStatus.submitted}
              </p>
            </div>
            <AlertTriangle className="h-6 w-6 md:h-8 md:w-8 text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Categories Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="text-center">
            <div className="text-base md:text-lg font-semibold text-blue-600 dark:text-blue-400">
              {stats.byCategory.direct_tax}
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Impôts Directs</div>
            <div className="text-xs text-muted-foreground hidden sm:block">
              IBP, IPR, IRCM, IRVM, IPF
            </div>
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="text-center">
            <div className="text-base md:text-lg font-semibold text-purple-600 dark:text-purple-400">
              {stats.byCategory.indirect_tax}
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Taxes Indirectes</div>
            <div className="text-xs text-muted-foreground hidden sm:block">
              TVA, TPI, TE, TCR, TAD, TRD
            </div>
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="text-center">
            <div className="text-base md:text-lg font-semibold text-orange-600 dark:text-orange-400">
              {stats.byCategory.social_contribution}
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Cotisations Sociales</div>
            <div className="text-xs text-muted-foreground hidden sm:block">
              CNSS, INPP, ONEM
            </div>
          </div>
        </div>

        <div className="bg-card p-3 md:p-4 rounded-lg shadow-sm border border-border">
          <div className="text-center">
            <div className="text-base md:text-lg font-semibold text-teal-600 dark:text-teal-400">
              {stats.byCategory.special_tax}
            </div>
            <div className="text-xs md:text-sm text-muted-foreground">Taxes Spécifiques</div>
            <div className="text-xs text-muted-foreground hidden sm:block">
              TSD, TPU, TSE, AUTRES
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width Table */}
      <div className="space-y-4">
        {/* Filters */}
        <DeclarationFilters 
          filters={filters}
          onFiltersChange={setFilters}
        />

        {/* Declarations List */}
        <Card title="Déclarations Fiscales" icon={FileSpreadsheet}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Chargement des déclarations...</span>
            </div>
          ) : (
            <DeclarationList
              declarations={safeDeclarations}
              filters={filters}
              onPreview={handlePreviewDeclaration}
              onDownload={(declaration) => handleDownload('pdf', declaration.reference || declaration.id)}
            />
          )}
        </Card>
      </div>

      {/* Summary Footer */}
      <Card title="Résumé des montants">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-lg md:text-xl font-bold text-blue-600 dark:text-blue-400">
              {format(stats.totalAmount)}
            </div>
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Montant Total des Déclarations
            </div>
          </div>
          <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <div className="text-lg md:text-xl font-bold text-orange-600 dark:text-orange-400">
              {format(stats.totalFees)}
            </div>
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Frais Supplémentaires
            </div>
          </div>
          <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <div className="text-lg md:text-xl font-bold text-red-600 dark:text-red-400">
              {format(stats.totalPenalties)}
            </div>
            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Pénalités de Retard
            </div>
          </div>
        </div>
      </Card>

      {/* Preview Modal */}
      {showPreviewModal && selectedDeclaration && (
        <Modal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          title={`Aperçu - ${selectedDeclaration.reference || selectedDeclaration.id}`}
          size="xl"
        >
          <DeclarationPreview
            declaration={selectedDeclaration}
            onDownload={() => handleDownload('pdf', selectedDeclaration.reference || selectedDeclaration.id)}
            onPrint={() => handlePrint()}
          />
        </Modal>
      )}

      {/* New Declaration Modal */}
      {showNewDeclarationModal && (
        <NewDeclarationModal
          isOpen={showNewDeclarationModal}
          onClose={() => setShowNewDeclarationModal(false)}
        />
      )}
    </div>
  );
}
